import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { Dropdown } from 'antd';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils/cn';

import styles from './action-menu.module.scss';

export type ActionMenuItem = {
  key: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

export type ActionMenuGroup = {
  label?: string;
  items: ActionMenuItem[];
};

export type ActionMenuProps = {
  groups: ActionMenuGroup[];
  children?: ReactNode;
  triggerLabel?: string;
  horizontal?: boolean;
  /** AC 118861 §I — a menu disabled while its page loads; the trigger stays in place so the header
   *  does not shift. */
  disabled?: boolean;
};

function itemLabel(item: ActionMenuItem) {
  return (
    <span className={styles.itemBody}>
      <span className={styles.itemTitle}>{item.label}</span>
      {item.description ? <span className={styles.itemDescription}>{item.description}</span> : null}
    </span>
  );
}

export function ActionMenu({
  groups,
  children,
  triggerLabel = 'Actions',
  horizontal = false,
  disabled = false,
}: ActionMenuProps) {
  const handlers = new Map(groups.flatMap((g) => g.items.map((i) => [i.key, i.onClick])));

  // A menu with nothing in it is not a menu: antd renders the trigger regardless and opens an
  // empty popup, which is exactly the "control that cannot work" this component is used to avoid.
  // Every caller assembles `items` conditionally — an order that is neither delivered nor has an
  // eligible device reaches zero (ADO 120855) — so the guard belongs here rather than repeated
  // across fifteen call sites. `children` is withheld too: it supplies the trigger, it is not a
  // reason to show one.
  if (groups.every((group) => group.items.length === 0)) return null;

  const items = groups.flatMap((group, index) => [
    ...(index > 0 ? [{ type: 'divider' as const, key: `divider-${index}` }] : []),
    ...(group.label ? [{ type: 'group' as const, key: `group-${index}`, label: group.label }] : []),
    ...group.items.map((item) => ({
      key: item.key,
      label: itemLabel(item),
      icon: item.icon,
      danger: item.danger,
      disabled: item.disabled,
    })),
  ]);

  return (
    <Dropdown
      trigger={['click']}
      disabled={disabled}
      menu={{ items, onClick: ({ key }) => handlers.get(key)?.() }}
      classNames={{ root: styles.popup }}
    >
      {children ?? (
        <button
          type="button"
          className={styles.trigger}
          aria-label={triggerLabel}
          disabled={disabled}
        >
          <DotsThreeVerticalIcon
            size={18}
            weight="bold"
            aria-hidden
            className={cn(horizontal && styles.horizontal)}
          />
        </button>
      )}
    </Dropdown>
  );
}
